import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'ui-file-input',
  templateUrl: './file-input.component.html',
  styleUrls: ['./file-input.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileInputComponent implements OnInit, OnDestroy {
  @Input() public label = '';
  @Input() public inputId = '';
  @Input() public availableFormats = '';
  @Input() public availableFormatsForErrorMessage = '';
  @Input() public multipleFiles = false;
  @Input() public control: FormControl<Array<File> | null> =
    new FormControl<Array<File> | null>(null);
  @Input() public removeCallback: (() => void) | null = null;
  @Input() public viewCallback: (() => void) | null = null;

  @ViewChild('fileInput') public fileInput:
    | ElementRef<HTMLInputElement>
    | undefined;

  private _destroy$ = new Subject<void>();

  public fileName = '';

  public ngOnInit(): void {
    if (this.control)
      this.control.valueChanges
        .pipe(takeUntil(this._destroy$))
        .subscribe((files) => {
          if (!files || files.length < 1) {
            return;
          }

          if (this.multipleFiles) {
            this.fileName = files
              .filter((f) => !!f)
              .map((f) => f.name)
              .join(', ');
            return;
          }

          const file = files[0];
          this.fileName = file?.name ?? '';
        });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onFileSelected(event: Event): void {
    const files = (event.target as HTMLInputElement).files;

    if (!files || files.length < 1) {
      return;
    }

    if (this.multipleFiles) {
      const fileArray: Array<File> = [];
      // note: FileList does not implement [Symbol.iterator]()
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0; i < files.length; i++) {
        const item = files[i];
        if (item) {
          fileArray.push(item);
        }
      }
      this.fileName = fileArray.map((f) => f.name).join(', ');
      this.control.patchValue(fileArray);
      return;
    }

    const file = files[0];
    this.fileName = file?.name ?? '';
    this.control.patchValue([file]);
  }

  public viewFile(): void {
    if (this.viewCallback) {
      this.viewCallback();
      return;
    }
  }

  public onRemoveFile(): void {
    if (this.removeCallback) {
      this.removeCallback();
      return;
    }
    this.removeFile();
  }

  public removeFile(): void {
    this.fileName = '';
    if (this.fileInput) this.fileInput.nativeElement.value = '';
    this.control.reset();
  }

  public chooseFiles(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();

    this.control.markAsTouched();
    this.fileInput?.nativeElement.click();
  }
}
